import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { ProductCard, Badge, money } from "./ProductCard";
import ProductGallery from "./ProductGallery";
import ProductReviews from "./ProductReviews";
import { swatchColor, isLightColor } from "@/lib/colorSwatch";
import { Truck, RotateCcw, Ruler, ShoppingCart, Eye, Check } from "lucide-react";
import { useCart } from "@/contexts/CartContext";

export default function MarketplaceProduct() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  const { addItem } = useCart();
  const [data, setData] = useState(undefined);
  const [selected, setSelected] = useState({});   // {Size: "Adult M"}
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/marketplace/product/${slug}`, { params: preview ? { preview: true } : {} })
      .then(({ data }) => {
        setData(data);
        const init = {};
        Object.entries(data.options || {}).forEach(([k, vals]) => { if (vals.length === 1) init[k] = vals[0]; });
        setSelected(init);
      }).catch(() => setData(null));
  }, [slug, preview]);

  const product = data?.product;
  const variants = data?.variants || [];
  const options = data?.options || {};

  // Which option drives the image gallery (default "Color" if present)
  const optionNames = Object.keys(options);
  const imageGroup = useMemo(() => {
    if (!product) return null;
    if (product.image_group && options[product.image_group]) return product.image_group;
    const colorish = optionNames.find((n) => /colou?r/i.test(n));
    return colorish || null;
  }, [product, options, optionNames]);

  // galleries keyed by the image-driving option value (e.g. Black → [images])
  const galleryMap = useMemo(() => {
    const m = {};
    (product?.variant_galleries || []).forEach((g) => {
      if (imageGroup && g.option_name === imageGroup) {
        m[g.option_value] = { images: g.images || [], swatch_hex: g.swatch_hex };
      }
    });
    return m;
  }, [product, imageGroup]);

  // Match a variant against current selection (all option names chosen)
  const matchedVariant = useMemo(() => {
    if (!variants.length) return null;
    if (optionNames.some((n) => !selected[n])) return null;
    return variants.find((v) => optionNames.every((n) => (v.option_values || {})[n] === selected[n])) || null;
  }, [variants, optionNames, selected]);

  // Choose an option value; preserve other groups, but clear any that become invalid.
  const chooseOption = (name, val) => {
    setSelected((prev) => {
      const next = { ...prev, [name]: val };
      optionNames.forEach((other) => {
        if (other === name || !next[other]) return;
        const ok = variants.some((v) =>
          optionNames.every((n) => {
            if (n === other) return (v.option_values || {})[n] === next[other];
            if (n === name) return (v.option_values || {})[n] === val;
            return !next[n] || (v.option_values || {})[n] === next[n];
          }));
        if (!ok) delete next[other];
      });
      return next;
    });
  };

  // Which option values are available given the OTHER selections (disable invalid combos)
  const isValueAvailable = (name, val) => {
    return variants.some((v) => {
      if ((v.option_values || {})[name] !== val) return false;
      return optionNames.every((n) => n === name || !selected[n] || (v.option_values || {})[n] === selected[n]);
    });
  };

  if (data === undefined) return <PublicLayout><div className="max-w-7xl mx-auto px-4 py-16 text-slate-400">Loading…</div></PublicLayout>;
  if (data === null) return <PublicLayout><div className="max-w-7xl mx-auto px-4 py-16 text-center"><p className="font-display text-2xl uppercase">Product not found</p><Link to="/marketplace/products" className="text-[#E63946] text-sm">Back to Shop</Link></div></PublicLayout>;

  const basePrice = product.price_cents;
  const price = matchedVariant?.price_cents_override ?? basePrice;
  const compare = matchedVariant?.compare_at_cents_override ?? product.compare_at_cents;
  const onSale = compare && compare > price;
  const productImages = (product.images || []).slice().sort((a, b) => (b.is_primary === true) - (a.is_primary === true) || (a.order ?? 0) - (b.order ?? 0));
  // colour-driven gallery: selected colour's images, else default product gallery
  const selectedColor = imageGroup ? selected[imageGroup] : null;
  const colorImages = selectedColor && galleryMap[selectedColor]?.images?.length ? galleryMap[selectedColor].images : null;
  const gallery = (colorImages || productImages).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const variantStock = matchedVariant ? (matchedVariant.inventory_tracked ? matchedVariant.inventory_qty : null) : null;
  const preorderish = product.fulfillment_model === "preorder" || product.fulfillment_model === "made_to_order";
  let stockMsg = null, stockTone = "text-slate-500";
  if (matchedVariant) {
    if (!matchedVariant.inventory_tracked) { stockMsg = product.fulfillment_model === "preorder" ? "Available for preorder" : product.fulfillment_model === "made_to_order" ? "Made to order" : "In stock"; stockTone = "text-emerald-600"; }
    else if (variantStock <= 0) { stockMsg = "Sold out"; stockTone = "text-[#E63946]"; }
    else if (variantStock <= (matchedVariant.low_stock_threshold || 5)) { stockMsg = `Only ${variantStock} left`; stockTone = "text-amber-600"; }
    else { stockMsg = "In stock"; stockTone = "text-emerald-600"; }
  }

  const needsSelection = Object.keys(options).length > 0 && !matchedVariant;
  const soldOut = matchedVariant && matchedVariant.inventory_tracked && variantStock <= 0;
  const canAdd = !preview && !!matchedVariant && !soldOut;
  const addLabel = preview ? "Preview mode" : needsSelection ? "Select options" : soldOut ? "Sold out" : product.fulfillment_model === "preorder" ? "Preorder Now" : "Add to Cart";
  const handleAdd = () => {
    if (!canAdd) return;
    addItem({ product_id: product.id, variant_id: matchedVariant.id, qty });
  };

  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="marketplace-product">
        {data.is_preview && <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-sm px-4 py-2 text-sm flex items-center gap-2" data-testid="preview-banner"><Eye className="w-4 h-4" /> Admin preview — this product is a DRAFT and not publicly visible.</div>}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Gallery */}
          <ProductGallery images={gallery} resetKey={selectedColor} productName={product.name} />

          {/* Info */}
          <div>
            <div className="flex flex-wrap gap-1.5">
              {onSale && <Badge tone="red">Sale</Badge>}
              {product.new_arrival && <Badge tone="green">New</Badge>}
              {product.limited_edition && <Badge tone="amber">Limited Edition</Badge>}
              {product.fulfillment_model === "preorder" && <Badge tone="navy">Preorder</Badge>}
              {product.fulfillment_model === "made_to_order" && <Badge tone="slate">Made to Order</Badge>}
            </div>
            <h1 className="font-display text-3xl uppercase tracking-tight mt-2">{product.name}</h1>
            {data.collections?.length > 0 && <div className="mt-1 flex gap-2 text-xs">{data.collections.map((c) => <Link key={c.id} to={`/marketplace/collection/${c.slug}`} className="text-[#E63946] hover:underline">{c.name}</Link>)}</div>}
            <div className="mt-3 flex items-baseline gap-3">
              <span className="font-data text-2xl text-[#0A192F]" data-testid="product-price">{money(price)}</span>
              {onSale && <><span className="text-slate-400 line-through" data-testid="product-compare">{money(compare)}</span><span className="text-emerald-600 text-sm font-semibold" data-testid="product-savings">Save {money(compare - price)}</span></>}
            </div>
            {matchedVariant?.sku && <div className="mt-1 text-xs text-slate-400" data-testid="product-sku">SKU: {matchedVariant.sku}</div>}
            {product.short_description && <p className="mt-3 text-slate-600">{product.short_description}</p>}

            {/* Options */}
            <div className="mt-5 space-y-4">
              {optionNames.map((name) => {
                const vals = options[name];
                const isImageGroup = name === imageGroup;
                return (
                  <div key={name} data-testid={`option-${name}`}>
                    <div className="label-overline text-[10px] mb-1.5">
                      {name}{isImageGroup && selected[name] ? <span className="text-slate-400 normal-case tracking-normal font-normal"> — {selected[name]}</span> : ""}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {vals.map((val) => {
                        const avail = isImageGroup
                          ? variants.some((v) => (v.option_values || {})[name] === val)   // colour always selectable if it exists
                          : isValueAvailable(name, val);
                        const active = selected[name] === val;
                        if (isImageGroup) {
                          const hex = swatchColor(val, galleryMap[val]?.swatch_hex);
                          const light = isLightColor(hex);
                          return (
                            <button key={val} type="button" data-testid={`option-${name}-${val}`} disabled={!avail}
                              onClick={() => chooseOption(name, val)}
                              title={val} aria-label={`${name}: ${val}${avail ? "" : " (unavailable)"}`} aria-pressed={active}
                              className={`relative w-9 h-9 rounded-full border transition-all ${active ? "ring-2 ring-offset-2 ring-[#0A192F] border-[#0A192F]" : "border-slate-300 hover:border-slate-500"} ${!avail ? "opacity-40 cursor-not-allowed" : ""}`}
                              style={{ backgroundColor: hex }}>
                              {active && <Check className={`w-4 h-4 absolute inset-0 m-auto ${light ? "text-[#0A192F]" : "text-white"}`} />}
                              {!avail && <span className="absolute inset-0 m-auto w-full h-[1px] bg-slate-400 rotate-45" />}
                            </button>
                          );
                        }
                        return (
                          <button key={val} type="button" data-testid={`option-${name}-${val}`} disabled={!avail}
                            onClick={() => chooseOption(name, val)}
                            aria-pressed={active} aria-label={`${name}: ${val}${avail ? "" : " (unavailable)"}`}
                            className={`px-3 py-1.5 text-sm rounded-sm border transition-colors ${active ? "bg-[#0A192F] text-white border-[#0A192F]" : avail ? "bg-white text-[#0A192F] border-slate-300 hover:border-[#0A192F]" : "bg-slate-50 text-slate-300 border-slate-200 line-through cursor-not-allowed"}`}>
                            {val}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Stock */}
            {stockMsg && <div className={`mt-4 text-sm font-semibold ${stockTone}`} data-testid="stock-status">{stockMsg}</div>}
            {product.fulfillment_model === "preorder" && product.preorder && (
              <div className="mt-2 text-xs text-slate-500" data-testid="preorder-info">
                {product.preorder.closing_date && <div>Preorder closes: {product.preorder.closing_date}</div>}
                {product.preorder.est_fulfillment_date && <div>Estimated fulfilment: {product.preorder.est_fulfillment_date}</div>}
              </div>
            )}

            {/* Qty + Add to cart (disabled until P1c) */}
            <div className="mt-5 flex items-center gap-3">
              <div className="flex items-center border border-slate-300 rounded-sm">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2 text-slate-500">−</button>
                <span data-testid="qty" className="px-3 font-data">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="px-3 py-2 text-slate-500">+</button>
              </div>
              <button data-testid="add-to-cart" onClick={handleAdd} disabled={!canAdd}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors ${canAdd ? "bg-[#E63946] hover:bg-[#c62f3b] text-white" : "bg-slate-200 text-slate-500 cursor-not-allowed"}`}>
                <ShoppingCart className="w-4 h-4" /> {addLabel}
              </button>
            </div>

            {/* Meta / policy links */}
            <div className="mt-6 border-t border-slate-200 pt-4 space-y-2 text-sm text-slate-600">
              <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-slate-400" /> {product.shipping_class === "pickup_only" ? "Local pickup only" : product.shipping_class === "free" ? "Free shipping" : "Standard shipping (Canada & US)"}</div>
              <div className="flex items-center gap-4">
                <Link to="/marketplace/products" className="inline-flex items-center gap-1 hover:text-[#E63946]"><Ruler className="w-4 h-4" /> Size Guide</Link>
                <Link to="/marketplace" className="inline-flex items-center gap-1 hover:text-[#E63946]"><RotateCcw className="w-4 h-4" /> Return Policy</Link>
              </div>
            </div>

            {product.full_description && <div className="mt-6 text-slate-700 whitespace-pre-line text-sm leading-relaxed">{product.full_description}</div>}
          </div>
        </div>

        {/* Customer reviews (full-width, below product details) */}
        {!preview && <ProductReviews slug={slug} />}

        {data.related?.length > 0 && (
          <section className="mt-14">
            <h2 className="font-display text-2xl uppercase tracking-tight">You May Also Like</h2>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">{data.related.map((p) => <ProductCard key={p.id} p={p} />)}</div>
          </section>
        )}
      </div>
    </PublicLayout>
  );
}
